import {
  Component,
  ElementRef,
  ViewChild,
  Input,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  inject,
  OnDestroy,
  ViewEncapsulation,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import * as d3 from 'd3';
import { ThemeService } from '../../../../core/services/theme.service';
import { Subscription } from 'rxjs';

export interface WatchTimeData {
  label: string;
  value: number;
  color?: string;
  id?: string;
}

@Component({
  selector: 'app-watch-time-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './watch-time-chart.component.html',
  styleUrls: ['./watch-time-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class WatchTimeChartComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef;

  @Input() data: WatchTimeData[] = [];

  // Configuration Inputs
  @Input() leftMargin: number = 100;
  @Input() rightMargin: number = 40;
  @Input() topMargin: number = 20;
  @Input() bottomMargin: number = 50;

  @Input() showYAxisGrid: boolean = true;
  @Input() showXAxisGrid: boolean = true;
  @Input() showXAxisLabel: boolean = true;
  @Input() showYAxisLabel: boolean = true;
  @Input() showYAxisLine: boolean = true;
  @Input() showBarsValue: boolean = false;
  @Input() xAxisTitle: string = 'Watch Time (Hours)';

  private currentTheme: 'light' | 'dark' = 'dark';
  private themeService = inject(ThemeService);
  private resizeObserver!: ResizeObserver;
  private themeSubscription?: Subscription;

  ngAfterViewInit() {
    this.currentTheme = this.themeService.getTheme();
    this.themeSubscription = this.themeService.theme$.subscribe((theme) => {
      this.currentTheme = theme;
      this.drawChart();
    });

    this.resizeObserver = new ResizeObserver(() => this.drawChart());
    if (this.chartContainer) {
      this.resizeObserver.observe(this.chartContainer.nativeElement);
    }
    // Initial draw
    setTimeout(() => this.drawChart(), 0);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.chartContainer?.nativeElement) {
      this.drawChart();
    }
  }

  ngOnDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.themeSubscription) this.themeSubscription.unsubscribe();
  }

  private drawChart() {
    const container = this.chartContainer.nativeElement;
    const parent = container.parentElement;

    if (!container || !parent) return;
    if (!this.data || this.data.length === 0) {
      d3.select(container).selectAll('*').remove();
      return;
    }

    // Cleanup
    d3.select(container).selectAll('*').remove();
    d3.select(parent).selectAll('.chart-tooltip').remove();

    const colors = this.getColors();

    // Dimensions
    // Height depends on data length to keep bars consistent size, or fixed height?
    // Let's use flexible height based on data
    const barStep = 60; // Space per bar
    const chartHeight = Math.max(
      container.clientHeight,
      this.data.length * barStep + this.topMargin + this.bottomMargin
    );
    // If container has specific height set by CSS, use it, otherwise use calculated.
    // Usually better to fit container if it exists.
    const height =
      container.clientHeight > 0
        ? container.clientHeight
        : this.data.length * barStep + this.topMargin + this.bottomMargin;
    const width = container.clientWidth;

    const margin = {
      top: this.topMargin,
      right: Math.max(this.rightMargin, 60), // Ensure at least 60px right margin
      bottom: this.bottomMargin,
      left: this.leftMargin,
    };

    const svg = d3
      .select(container)
      .append('svg')
      .attr('width', width)
      .attr('height', height);

    // X Scale
    const maxVal = d3.max(this.data, (d) => d.value) || 0;
    // Add 15% padding to ensure bar doesn't touch the edge even with rounded corners
    const domainMax = maxVal === 0 ? 10 : Math.ceil(maxVal * 1.15);

    const x = d3
      .scaleLinear()
      .domain([0, domainMax])
      .range([margin.left, width - margin.right]);

    // Create evenly spaced ticks (5 ticks = 0%, 25%, 50%, 75%, 100%)
    const tickCount = 5;
    const ticks = Array.from(
      { length: tickCount },
      (_, i) => (domainMax / (tickCount - 1)) * i
    );

    // Y Scale
    const y = d3
      .scaleBand()
      .domain(this.data.map((d) => d.label))
      .range([margin.top, height - margin.bottom])
      .padding(0.4);

    // Gridlines
    if (this.showXAxisGrid) {
      svg
        .append('g')
        .attr('class', 'grid x-grid')
        .attr('transform', `translate(0,${height - margin.bottom})`)
        .call(
          d3
            .axisBottom(x)
            .tickValues(ticks)
            .tickSize(-(height - margin.top - margin.bottom))
            .tickFormat(() => '')
        )
        .call((g) => g.select('.domain').remove())
        .call((g) =>
          g
            .selectAll('.tick line')
            .attr('stroke', colors.gridLine)
            .attr('stroke-dasharray', '4,4')
            .attr('stroke-opacity', 0.3)
        );

      // Add closing vertical grid line at the right edge
      svg
        .append('line')
        .attr('class', 'x-grid-line-end')
        .attr('x1', width - margin.right)
        .attr('x2', width - margin.right)
        .attr('y1', margin.top)
        .attr('y2', height - margin.bottom)
        .attr('stroke', colors.gridLine)
        .attr('stroke-dasharray', '4,4')
        .attr('stroke-opacity', 0.3);
    }

    if (this.showYAxisGrid) {
      svg
        .append('g')
        .attr('class', 'grid y-grid')
        .attr('transform', `translate(${margin.left},0)`)
        .call(
          d3
            .axisLeft(y)
            .tickSize(-(width - margin.left - margin.right))
            .tickFormat(() => '')
        )
        .call((g) => g.select('.domain').remove())
        .call((g) =>
          g
            .selectAll('.tick line')
            .attr('stroke', colors.gridLine)
            .attr('stroke-dasharray', '4,4')
            .attr('stroke-opacity', 0.3)
        );

      // Manual top grid line to close the chart
      svg
        .append('line')
        .attr('class', 'y-grid-line-top')
        .attr('x1', margin.left)
        .attr('x2', width - margin.right)
        .attr('y1', margin.top)
        .attr('y2', margin.top)
        .attr('stroke', colors.gridLine)
        .attr('stroke-dasharray', '4,4')
        .attr('stroke-opacity', 0.3);
    }

    // Axes
    if (this.showXAxisLabel) {
      const xAxis = svg
        .append('g')
        .attr('class', 'x-axis')
        .attr('transform', `translate(0,${height - margin.bottom})`)
        .call(
          d3
            .axisBottom(x)
            .tickValues(ticks)
            .tickFormat((d) => d3.format(',')(d as number))
        );

      xAxis.select('.domain').attr('stroke', colors.axisLine);

      xAxis.selectAll('.tick line').attr('stroke', colors.axisLine);

      xAxis
        .selectAll('.tick text')
        .attr('fill', colors.text)
        .style('font-size', '12px')
        .style('font-family', 'Inter, sans-serif');

      // Axis Title
      //   if (this.xAxisTitle) {
      //     svg
      //       .append('text')
      //       .attr('class', 'x-axis-title')
      //       .attr('x', margin.left + (width - margin.left - margin.right) / 2)
      //       .attr('y', height - 10) // Near bottom
      //       .attr('text-anchor', 'middle')
      //       .attr('fill', colors.text)
      //       .style('font-size', '14px')
      //       .style('font-family', 'Inter, sans-serif')
      //       .text(this.xAxisTitle);
      //   }
    }

    if (this.showYAxisLabel) {
      const yAxis = svg
        .append('g')
        .attr('class', 'y-axis')
        .attr('transform', `translate(${margin.left},0)`)
        .call(d3.axisLeft(y).tickSize(0)); // Hide ticks

      if (this.showYAxisLine) {
        yAxis.select('.domain').attr('stroke', colors.axisLine);
      } else {
        yAxis.select('.domain').remove(); // Hide Y axis line usually
      }

      // Colorize labels
      const labelColorMap = new Map(this.data.map((d) => [d.label, d.color]));

      yAxis
        .selectAll('.tick text')
        .attr('x', -10)
        .attr('fill', (d: any) => labelColorMap.get(d) || colors.text) // Use data color
        .style('font-size', '14px')
        .style('font-weight', '500')
        .style('font-family', 'Inter, sans-serif');
    }

    // Bars
    // Unique gradient per bar? Or just fill color. Image shows solid colors.

    // Tooltip
    const tooltip = d3
      .select(parent)
      .append('div')
      .attr('class', 'chart-tooltip')
      .style('opacity', 0)
      .style('position', 'absolute')
      .style('background', colors.tooltipBg)
      .style('border', `1px solid ${colors.tooltipBorder}`)
      .style('color', colors.tooltipText)
      .style('padding', '8px 12px')
      .style('border-radius', '8px')
      .style('font-family', 'Inter, sans-serif')
      .style('font-size', '12px')
      .style('pointer-events', 'none')
      .style('box-shadow', '0 4px 6px -1px rgba(0, 0, 0, 0.1)');

    const bars = svg
      .selectAll('.bar')
      .data(this.data)
      .enter()
      .append('path') // Using path for rounded corners
      .attr('class', 'bar')
      .attr('fill', (d) => d.color || colors.primary)
      .attr('d', (d) => {
        const barY = y(d.label) || 0;
        const barHeight = y.bandwidth();
        // Ensure a minimum width of 5px so small values are visible
        const barWidth = Math.max(5, x(d.value) - margin.left);
        // Radius should not exceed half the width for very thin bars
        const r = Math.min(6, barWidth / 2, barHeight / 2);

        // Path for rect with rounded right corners
        // Start top-left
        return `
          M ${margin.left} ${barY}
          H ${margin.left + barWidth - r}
          Q ${margin.left + barWidth} ${barY} ${margin.left + barWidth} ${
          barY + r
        }
          V ${barY + barHeight - r}
          Q ${margin.left + barWidth} ${barY + barHeight} ${
          margin.left + barWidth - r
        } ${barY + barHeight}
          H ${margin.left}
          Z
        `;
      })
      .on('mouseover', function (event, d) {
        d3.select(this).style('opacity', 0.8);
        tooltip.transition().duration(200).style('opacity', 1);
        tooltip.html(`
          <div style="font-size: 15px;font-weight: 600; margin-bottom: 4px;">${
            d.label
          }</div>
          <div style="font-size:16px;font-weight:400;margin-bottom:2px;color:${
            colors.tooltipText
          }">${d.value.toLocaleString() + ' ' + (d?.value > 1 ? 'Hours' : 'Hour')}</div>
        `);
      })
      .on('mousemove', function (event) {
        const [mx, my] = d3.pointer(event, parent);
        tooltip.style('left', mx + 10 + 'px').style('top', my - 28 + 'px');
      })
      .on('mouseout', function () {
        d3.select(this).style('opacity', 1);
        tooltip.transition().duration(200).style('opacity', 0);
      });

    // Bar Values
    if (this.showBarsValue) {
      svg
        .selectAll('.bar-value')
        .data(this.data)
        .enter()
        .append('text')
        .attr('class', 'bar-value')
        .attr('x', (d) => x(d.value) + 8)
        .attr('y', (d) => (y(d.label) || 0) + y.bandwidth() / 2)
        .attr('dy', '0.35em')
        .attr('fill', colors.text)
        .style('font-size', '12px')
        .text((d) => d.value.toLocaleString());
    }
  }

  private getColors() {
    const isDark = this.currentTheme === 'dark';
    return {
      text: isDark ? '#e4e4e7' : '#1E293B',
      axisLine: isDark ? '#47474e' : '#CBD5E1',
      gridLine: isDark ? '#2e2e33' : '#E2E8F0',
      tooltipBg: isDark ? '#1e1e21' : '#FFFFFF',
      tooltipBorder: isDark ? '#2e2e33' : '#E2E8F0',
      tooltipText: isDark ? '#fafafa' : '#0F172A',
      primary: '#3B82F6',
    };
  }
}
